import { Vue, Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import UrlMixin from '../query/url-mixin';
import { AclAccessLevelEnum, AclMemberRole, AclRole } from '@/services/models/Enums';
import { get as  _get, set as _set} from 'lodash';
import pageAccessSettings from './page-access-settings';
import store from '@/store';

@Component
export class AclMixin extends mixins(UrlMixin) {
  readonly reportTransform: { [type: string]: { single: string, plural: string} } = {
    'landings': {
      single: 'landing',
      plural: 'landings'
    },
    'campaigns': {
      single: 'campaign',
      plural: 'campaigns'
    },
    'offers': {
      single: 'offer',
      plural: 'offers'
    },
    'flows': {
      single: 'flow',
      plural: 'flows'
    },
    'traffic-sources': {
      single: 'trafficSource',
      plural: 'trafficSources'
    },
    'affiliate-networks': {
      single: 'affiliateNetwork',
      plural: 'affiliateNetworks'
    },
  };

  readonly aclPageAccessSettings = pageAccessSettings;

  getAclSection({single = true, type = this.$route.params.type } = {}): string {
    let result = 'default';

    if(this.isReport) {
      const reportTransform = this.reportTransform[type];
      if(reportTransform) {
        result = single
          ? reportTransform.single
          : reportTransform.plural;
      }
    }

    return result;
  }

  acl({workspaceId, type}:{workspaceId:string, type: string}) {
    const _default = {'*': AclAccessLevelEnum.deny};
    if(!workspaceId) return _default;

    const aclSection = this.getAclSection({ type });
    const workspaceAcl = store.state.acl[workspaceId].acl;
    return  workspaceAcl[aclSection]
      ? workspaceAcl[aclSection]
      : _default;
  }

  aclGetEditItemRights(data: any): string {
    const { workspaceId, id } = data;
    if(!workspaceId || !id) return;

    if(!this.isMemberRoleUser || store.state.acl[workspaceId].accessToExistingItems === AclAccessLevelEnum.fullAccess) {
      return AclAccessLevelEnum.fullAccess;
    }

    const aclWorkspace = this.acl(data)
    return aclWorkspace[id] ? aclWorkspace[id] : aclWorkspace['*'];
  }

  aclCanEditItem(data:any): boolean {
    return this.aclGetEditItemRights(data) === AclAccessLevelEnum.fullAccess;
  }

  aclReadOnly(data:any): boolean {
    const { workspaceId, id, type } = data;
    if(!this.isMemberRoleUser || !workspaceId || !id) return;

    if(store.state.acl[workspaceId].accessToExistingItems === AclAccessLevelEnum.fullAccess) return;

    const aclWorkspace = this.acl({workspaceId, type});
    return this.isMemberRoleUser && (aclWorkspace[id] || aclWorkspace['*']) === AclAccessLevelEnum.readOnly;
  }

  aclCanViewItem(data:any): boolean {
    return this.aclGetEditItemRights(data) === AclAccessLevelEnum.fullAccess
      || this.aclGetEditItemRights(data) === AclAccessLevelEnum.readOnly;
  }

  get aclCanCreate(): boolean {
    return !this.isMemberRoleUser || store.state.user.canCreate[this.getAclSection({ single: false })];
  }

  aclCanCreateOnSection({workspaceId,section}:{workspaceId:string,section?:string}): boolean {
    if(!workspaceId) return;
    if(!this.isMemberRoleUser) return true;

    const aclSection = section || this.getAclSection({ single: false });
    const workspaceAcl = store.state.acl && store.state.acl[workspaceId];
    return workspaceAcl && workspaceAcl.canCreate[aclSection]
  }

  get userRole() {
    return store.state.user.isMember
      ? AclRole.member
      : AclRole.user;
  }

  get isMemberRoleUser() {
    return store.state.user.role === 'user';
  }

  get memberRole() {
    if (store.state.user.role === 'user'){
      return AclMemberRole.memberUser
    }
    if (store.state.user.role === 'admin'){
      return AclMemberRole.memberAdmin
    }
    return false;
  }

  get isUserOrMemberAdmin() {
    return !store.state.user.isMember || (store.state.user.isMember && store.state.member && store.state.member.role === 'admin');
  }

  canAccessPage(page: string) {
    if (page === 'profile.custom-conversions') {
      const user = store.state.user;
      if (!user?.isMember) {
        return true;
      }
      return !!user.accessCustomConversions;
    }

    let accessRights = _get(this.aclPageAccessSettings, page, this.aclPageAccessSettings['*']);
    const result = ()=>{
      if(typeof accessRights === 'string'){
        return accessRights;
      }
      if(this.memberRole && accessRights[this.memberRole]){
        return accessRights[this.memberRole]
      }
      if(accessRights[this.userRole]){
        return accessRights[this.userRole]
      }
      if(accessRights['*']){
        return accessRights['*']
      }
     
      return this.aclPageAccessSettings['*']
    }
    

    return result() !== AclAccessLevelEnum.deny;
  }
}

export default new AclMixin();
